import { useEffect, useState } from 'react';
import { KeyRound, Monitor, Smartphone, Trash2 } from 'lucide-react';
import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser';
import { useI18n } from '../i18n';
import { describeUserAgent } from '../lib/device';
import { api } from '../sync/api';
import { Sheet } from '../ui/Sheet';
import { useToast } from '../ui/Toast';

interface SessionRow {
  id: string;
  current: boolean;
  createdAt: string;
  lastSeenAt: string;
  userAgent: string | null;
}

interface PasskeyRow {
  id: string;
  createdAt: string;
  lastUsedAt: string | null;
  transports: string[];
}

type RegOptions = Parameters<typeof startRegistration>[0]['optionsJSON'];

/** Carga una lista del servidor con estados de carga y error (sin conexión, por ejemplo). */
function useRemoteList<T>(path: string, key: string) {
  const [items, setItems] = useState<T[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let alive = true;
    api<Record<string, T[]>>(path)
      .then((res) => {
        if (!alive) return;
        setItems(res[key] ?? []);
        setFailed(false);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [path, key, version]);
  return { items, failed, reload: () => setVersion((v) => v + 1) };
}

/** Fecha corta: sin año si es el actual ("19 oct, 5:57"). */
function useDateFormat() {
  const { tag } = useI18n();
  return (iso: string) => {
    const d = new Date(iso);
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return new Intl.DateTimeFormat(tag, {
      day: 'numeric',
      month: 'short',
      year: sameYear ? undefined : 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(d);
  };
}

const MOBILE_OS = new Set(['iPhone', 'iPad', 'Android']);

export function SessionsSection() {
  const { t } = useI18n();
  const toast = useToast();
  const fmt = useDateFormat();
  const { items, failed, reload } = useRemoteList<SessionRow>('/auth/sessions', 'sessions');
  const [busy, setBusy] = useState(false);

  const label = (s: SessionRow) => {
    const d = describeUserAgent(s.userAgent);
    if (d.browser && d.os) return t('security.deviceOn', { browser: d.browser, os: d.os });
    return d.browser ?? d.os ?? t('security.unknownDevice');
  };

  const revoke = async (s: SessionRow) => {
    setBusy(true);
    try {
      await api(`/auth/sessions/${s.id}`, { method: 'DELETE', body: {} });
      toast.show(t('security.sessionRevoked'));
    } catch {
      toast.show(t('security.actionFailed'));
    } finally {
      setBusy(false);
      reload();
    }
  };

  const revokeOthers = async () => {
    setBusy(true);
    try {
      const r = await api<{ revoked: number }>('/auth/logout-others', { body: {} });
      toast.show(t('security.othersRevoked', { count: r.revoked }));
    } catch {
      toast.show(t('security.actionFailed'));
    } finally {
      setBusy(false);
      reload();
    }
  };

  const others = items?.filter((s) => !s.current).length ?? 0;

  return (
    <section className="stack" aria-labelledby="sessions-title">
      <h2 id="sessions-title">{t('security.sessionsTitle')}</h2>
      <p className="muted small">{t('security.sessionsHelp')}</p>
      {failed && (
        <p className="field__error" role="alert">
          {t('security.loadFailed')}{' '}
          <button type="button" className="btn btn--ghost btn--sm" onClick={reload}>
            {t('security.retry')}
          </button>
        </p>
      )}
      {items && (
        <ul className="list-card" style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label={t('security.sessionsTitle')}>
          {items.map((s) => (
            <li key={s.id} className="list-item">
              {MOBILE_OS.has(describeUserAgent(s.userAgent).os ?? '') ? <Smartphone size={20} aria-hidden="true" /> : <Monitor size={20} aria-hidden="true" />}
              <div className="list-item__body">
                <div className="list-item__title">
                  {label(s)} {s.current && <span className="badge badge--accent">{t('security.thisDevice')}</span>}
                </div>
                <div className="list-item__sub">{t('security.sessionDates', { last: fmt(s.lastSeenAt), created: fmt(s.createdAt) })}</div>
              </div>
              {!s.current && (
                <button
                  type="button"
                  className="btn btn--outline btn--sm"
                  disabled={busy}
                  aria-label={t('security.revokeNamed', { device: label(s) })}
                  onClick={() => void revoke(s)}
                >
                  {t('security.revoke')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {others > 0 && (
        <button type="button" className="btn btn--outline" disabled={busy} onClick={() => void revokeOthers()}>
          {t('security.revokeOthers')}
        </button>
      )}
    </section>
  );
}

export function PasskeysSection() {
  const { t } = useI18n();
  const toast = useToast();
  const fmt = useDateFormat();
  const { items, failed, reload } = useRemoteList<PasskeyRow>('/auth/passkeys', 'passkeys');
  const [confirm, setConfirm] = useState<PasskeyRow | null>(null);

  const add = async () => {
    try {
      const options = await api<RegOptions>('/auth/passkey/register/options', { body: {} });
      const response = await startRegistration({ optionsJSON: options });
      await api('/auth/passkey/register/verify', { body: response });
      toast.show(t('sync.passkeyAdded'));
    } catch {
      toast.show(t('sync.errors.passkey_failed'));
    } finally {
      reload();
    }
  };

  const remove = async (p: PasskeyRow) => {
    setConfirm(null);
    try {
      await api(`/auth/passkeys/${encodeURIComponent(p.id)}`, { method: 'DELETE', body: {} });
      toast.show(t('security.passkeyRemoved'));
    } catch {
      toast.show(t('security.actionFailed'));
    } finally {
      reload();
    }
  };

  return (
    <section className="stack" aria-labelledby="passkeys-title">
      <h2 id="passkeys-title">{t('security.passkeysTitle')}</h2>
      <p className="muted small">{t('security.passkeysHelp')}</p>
      <p className="muted small">{t('security.passkeysWhere')}</p>
      {failed && (
        <p className="field__error" role="alert">
          {t('security.loadFailed')}{' '}
          <button type="button" className="btn btn--ghost btn--sm" onClick={reload}>
            {t('security.retry')}
          </button>
        </p>
      )}
      {items && items.length === 0 && <p className="small">{t('security.noPasskeys')}</p>}
      {items && items.length > 0 && (
        <ul className="list-card" style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label={t('security.passkeysTitle')}>
          {items.map((p, i) => (
            <li key={p.id} className="list-item">
              <KeyRound size={20} aria-hidden="true" />
              <div className="list-item__body">
                <div className="list-item__title">{t('security.passkeyName', { n: items.length - i })}</div>
                <div className="list-item__sub">
                  {p.lastUsedAt
                    ? t('security.passkeyDates', { created: fmt(p.createdAt), used: fmt(p.lastUsedAt) })
                    : t('security.passkeyCreated', { created: fmt(p.createdAt) })}
                </div>
              </div>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                aria-label={t('security.removePasskeyNamed', { name: t('security.passkeyName', { n: items.length - i }) })}
                onClick={() => setConfirm(p)}
              >
                <Trash2 size={18} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {browserSupportsWebAuthn() && (
        <button type="button" className="btn btn--outline" onClick={() => void add()}>
          <KeyRound size={18} aria-hidden="true" /> {t('sync.addPasskey')}
        </button>
      )}
      <Sheet open={confirm !== null} onClose={() => setConfirm(null)} title={t('security.removePasskeyTitle')} description={t('security.removePasskeyBody')}>
        <button type="button" className="btn btn--danger btn--block" onClick={() => void remove(confirm!)}>
          {t('security.removePasskey')}
        </button>
        <button type="button" className="btn btn--ghost btn--block" onClick={() => setConfirm(null)}>
          {t('common.cancel')}
        </button>
      </Sheet>
    </section>
  );
}
